import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Gavel as GavelIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import api from "../services/api";

interface Stats {
  totalOrders: number;
  totalAppeals: number;
  totalProviders: number;
  totalMerchants: number;
  systemBalance: number;
  exchangeRates: any;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalOrders: 0,
    totalAppeals: 0,
    totalProviders: 0,
    totalMerchants: 0,
    systemBalance: 0,
    exchangeRates: {},
  });
  const [, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [orders, appeals, providers, merchants, rates] = await Promise.all([
        api.getOrders({ limit: 1 }),
        api.getAppeals({ limit: 1 }),
        api.getProviders(),
        api.getMerchants(),
        api.getExchangeRates(),
      ]);

      setStats({
        totalOrders: orders.total || 0,
        totalAppeals: appeals.total || 0,
        totalProviders: Array.isArray(providers) ? providers.length : 0,
        totalMerchants: Array.isArray(merchants) ? merchants.length : 0,
        systemBalance: 0,
        exchangeRates: rates[0] || {},
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Orders",
      value: stats.totalOrders,
      icon: <ReceiptIcon />,
      color: "#00bcd4",
    },
    {
      title: "Total Appeals",
      value: stats.totalAppeals,
      icon: <GavelIcon />,
      color: "#ff4081",
    },
    {
      title: "Active Providers",
      value: stats.totalProviders,
      icon: <AccountBalanceIcon />,
      color: "#4caf50",
    },
    {
      title: "Active Merchants",
      value: stats.totalMerchants,
      icon: <TrendingUpIcon />,
      color: "#ff9800",
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        VangardPay Test Dashboard
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: card.color,
                      color: "white",
                      mr: 2,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography color="textSecondary" gutterBottom>
                    {card.title}
                  </Typography>
                </Box>
                <Typography variant="h4">{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Exchange Rates
            </Typography>
            {stats.exchangeRates && (
              <List>
                <ListItem>
                  <ListItemText
                    primary="USD/RUB"
                    secondary={stats.exchangeRates.usdToRub || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="EUR/RUB"
                    secondary={stats.exchangeRates.eurToRub || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="USDT/RUB"
                    secondary={stats.exchangeRates.usdtToRub || "N/A"}
                  />
                </ListItem>
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button variant="contained" href="/orders" fullWidth>
                Create Test Order
              </Button>
              <Button
                variant="contained"
                color="secondary"
                href="/appeals"
                fullWidth
              >
                Create Test Appeal
              </Button>
              <Button variant="outlined" href="/api-test" fullWidth>
                API Testing Console
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Test Features
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip label="Order Creation" color="primary" />
              <Chip label="Appeal Management" color="secondary" />
              <Chip label="File Upload" />
              <Chip label="Provider Testing" />
              <Chip label="Merchant API" />
              <Chip label="Exchange Rates" />
              <Chip label="Balance Calculations" />
              <Chip label="Webhook Testing" />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
